import React, { useCallback, useState } from 'react';
import { Box, SxProps, useTheme } from '@mui/material';
import { useLayoutContext } from './context';

const DEFAULT_DRAWER_WIDTH = 1100;

interface BoxResizableProps {
  children?: React.ReactNode;
  storageKey: string;
  sx?: SxProps;
  minWidth?: number;
  maxWidth?: number;
  minHeight?: number;
  maxHeight?: number;
  defaultHeight?: number;
  defaultWidth?: number;
  disabled?: boolean;
  anchor?: 'right' | 'left' | 'bottom';
  drawerWidth?: number;
}

export const BoxResizable: React.FC<BoxResizableProps> = ({
  children,
  storageKey,
  sx = {},
  minWidth = 660,
  maxWidth = 2000,
  minHeight = 200,
  maxHeight = 1000,
  defaultHeight = 400,
  defaultWidth,
  disabled = false,
  anchor = 'right',
  drawerWidth = DEFAULT_DRAWER_WIDTH,
  ...boxProps
}) => {
  const theme = useTheme();
  const isVertical = anchor === 'bottom';

  const {
    layoutWidths: { [storageKey]: storedSize = isVertical ? defaultHeight : defaultWidth || drawerWidth },
    setLayoutWidths: setStoredSize,
  } = useLayoutContext();

  const [tempSize, setTempSize] = useState(storedSize);

  const getNewSize = useCallback(
    (e: React.MouseEvent | React.TouchEvent | any) => {
      const event = e.changedTouches?.[0] || e;
      if (isVertical) {
        const newHeight = document.body.offsetHeight - event.clientY;
        return Math.min(Math.max(newHeight, minHeight), maxHeight);
      }

      const newWidth = anchor === 'right' ? document.body.offsetWidth - event.clientX : event.clientX;
      return Math.min(Math.max(newWidth, minWidth), maxWidth);
    },
    [anchor, minWidth, maxWidth, minHeight, maxHeight, isVertical]
  );

  const updateLayoutSize = useCallback(
    (key, newSize) => {
      setStoredSize(key, newSize);
      window.dispatchEvent(new Event('onSidePanelResize'));
    },
    [setStoredSize]
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      setTempSize(getNewSize(e));
      e.preventDefault();
      window.dispatchEvent(new Event('onSidePanelResizing'));
    },
    [getNewSize]
  );

  const handleMouseUp = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (storageKey) {
        updateLayoutSize(storageKey, getNewSize(e));
      }
      document.removeEventListener('mouseup', handleMouseUp, true);
      document.removeEventListener('touchend', handleMouseUp, true);
      document.removeEventListener('mousemove', handleMouseMove, true);
      document.removeEventListener('touchmove', handleMouseMove, { passive: false } as AddEventListenerOptions);
    },
    [storageKey, getNewSize, updateLayoutSize, handleMouseMove]
  );

  const handleMouseDown = useCallback(() => {
    document.addEventListener('mouseup', handleMouseUp, true);
    document.addEventListener('touchend', handleMouseUp, true);
    document.addEventListener('mousemove', handleMouseMove, true);
    document.addEventListener('touchmove', handleMouseMove, { passive: false });
  }, [handleMouseUp, handleMouseMove]);

  const sizeSx = isVertical ? { height: (sx as any).height || tempSize } : { width: (sx as any).width || tempSize };

  const handleDoubleClick = useCallback(() => {
    const defaultSize = isVertical ? defaultHeight : defaultWidth || drawerWidth;
    updateLayoutSize(storageKey, defaultSize);
    setTempSize(defaultSize);
  }, [isVertical, defaultHeight, defaultWidth, storageKey, updateLayoutSize]);

  const handleSx: SxProps = {
    position: 'absolute',
    zIndex: 1,
    transition: theme.transitions.create(['border-color'], {
      duration: theme.transitions.duration.short,
    }),
    pointerEvents: disabled ? 'none' : undefined,
    '&:hover': {
      ...(isVertical
        ? { borderTopColor: 'var(--color-content-interactive-primary-default)' }
        : { borderColor: 'var(--color-content-interactive-primary-default)' }),
    },
    ...(isVertical
      ? {
          height: 8,
          width: '100%',
          top: 0,
          cursor: 'ns-resize',
          borderTop: '2px solid transparent',
        }
      : {
          width: 8,
          height: '100%',
          ...(anchor === 'right' ? { left: 0 } : { right: 0 }),
          cursor: 'ew-resize',
          borderLeft: '2px solid transparent',
        }),
  };

  return (
    <Box sx={{ ...sx, ...sizeSx, position: 'relative' }} {...boxProps}>
      <Box
        aria-label="resize-handle"
        sx={handleSx}
        onMouseDown={e => {
          handleMouseDown();
          e.preventDefault();
        }}
        onTouchStart={handleMouseDown}
        onDoubleClick={handleDoubleClick}
      />
      {children}
    </Box>
  );
};
